import {
  type ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
  useReducer,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  generate,
  gradeExercise,
  dictationRatingFromAccuracy,
  rhythmicDictationRating,
  midiToScaleDegree,
  type AudioPlan,
  type Exercise,
  type ExerciseAnswer,
  type GradingResult,
  type DictationGrading,
  type RhythmicDictationGrading,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  ExerciseCard,
  SolfegeButtons,
  MultipleChoiceOptions,
  StagePips,
  FeedbackBox,
  AudioStatusIndicator,
  PianoKeyboard,
  SessionSummary,
} from "@music-app/ui-components";
import {
  ratingFromResponse,
  buildAllItems,
  buildSession,
  MASTERY_PRESETS,
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type CurriculumNode,
  type CardStore,
  type Item,
  type Rating,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  startSession as dbStartSession,
  endSession as dbEndSession,
  recordReview as dbRecordReview,
  updateSessionAfterReview as dbUpdateSessionAfterReview,
  getActiveSession as dbGetActiveSession,
  recordBypassQuizPassed,
  type SessionRecord,
} from "@music-app/persistence";
import {
  practiceReducer,
  initialPracticeState,
  canAnswer,
  canReplay,
} from "./review-machine.js";
import { getRenderer } from "../lib/exercise-renderers/registry.js";
import {
  sessionReducer,
  initialSessionState,
  sessionStats,
  isSessionActive,
} from "./session-machine.js";
import { filterByPrefs, buildSessionConfig } from "./session-prefs.js";
import { useKeyboardShortcuts, type ShortcutMap } from "../lib/use-keyboard-shortcuts.js";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { VocalSinger } from "./VocalSinger.js";
import { IntroducingBanner } from "./IntroducingBanner.js";
import {
  createMidiInput,
  type MidiInput,
  type MidiNoteOn,
  type MidiDeviceInfo,
} from "@music-app/input-midi";
import {
  gradeMidiAnswer,
  isMidiSupportedExercise,
} from "./midi-grading.js";
import type { PlanStop } from "../lib/coach-plan.js";
import { PageHeader } from "../components/PageHeader.js";
import { FeedbackModal } from "../components/FeedbackModal.js";

const CADENCE_PIP_COUNT = 4;

// Mid-session adaptive pacing (Feature 6). Evaluated every N reviews.
const REBALANCE_INTERVAL = 10;
const REBALANCE_MAX_SWAP = 3;
const REBALANCE_AGAIN_HIGH = 0.3;
const REBALANCE_AGAIN_LOW = 0.05;
const ITEMS_PER_MINUTE = 6; // matches buildSessionConfig

type AudioStatus = "loading" | "ready" | "synth-fallback" | "error";

/**
 * Practice page. Two state machines compose here:
 *   review-machine.ts owns the per-exercise lifecycle
 *     (ready→playing→awaiting→graded).
 *   session-machine.ts owns the session lifecycle
 *     (idle→running→summary), the queue produced by buildSession(), and
 *     the running stats persisted to IndexedDB at session end.
 *
 * Audio engine status (loading piano samples) and per-note animation
 * state (active keys, pip index) are orthogonal useState since they
 * update on every audio event.
 */
export function ReviewSessionPage(): ReactNode {
  const [practice, practiceDispatch] = useReducer(practiceReducer, initialPracticeState);
  const [session, sessionDispatch] = useReducer(sessionReducer, initialSessionState);
  const { phase, exercise, counts } = practice;

  const [audioStatus, setAudioStatus] = useState<AudioStatus>("loading");
  const [pianoProgress, setPianoProgress] = useState<number | undefined>(undefined);
  /** True while ensureAudio() is running its first init + sample load.
   *  Drives the Start button's disabled state and label so the user
   *  knows samples are still arriving rather than silently waiting. */
  const [audioInitiating, setAudioInitiating] = useState(false);

  const [activePip, setActivePip] = useState<number | null>(null);

  type PendingConfidence = { ex: Exercise; correct: boolean; rating: Rating; responseMs: number; chosenAnswerId: string | null };
  const [awaitingConfidence, setAwaitingConfidence] = useState(false);
  const [confidenceCountdown, setConfidenceCountdown] = useState(0);
  const pendingConfidenceRef = useRef<PendingConfidence | null>(null);
  // Stable ref to latest resolve handler so the countdown effect doesn't
  // need it as a dependency (avoids re-binding the timer on every render).
  const confidenceResolveRef = useRef<(unsure: boolean) => void>(() => {});
  // Stable ref for the quiz timer auto-submit (same pattern as confidence).
  const quizAnswerRef = useRef<(optionId: string) => void>(() => {});
  const [donePips, setDonePips] = useState<number[]>([]);
  const [activeMidis, setActiveMidis] = useState<ReadonlySet<number>>(() => new Set());

  const playerRef = useRef<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);
  const bassIndexRef = useRef<number>(0);

  /** MIDI input runtime (Phase 2A C.2). One MidiInput per route mount;
   *  lazy-initialized on the first mount where `midiInputEnabled` is on
   *  AND a supported exercise is in flight. The buffer holds recent
   *  noteons for the current exercise and is reset on every new
   *  exercise + on grading. The submit-guard ref prevents the same
   *  exercise from being auto-graded twice when the user keeps
   *  pressing keys after a chord identification fires. */
  const midiRef = useRef<MidiInput | null>(null);
  const midiNotesRef = useRef<MidiNoteOn[]>([]);
  const midiSubmittedRef = useRef(false);
  const [midiDevices, setMidiDevices] = useState<MidiDeviceInfo[]>([]);
  /** In-flight ensureAudio() promise. Used to dedupe concurrent calls
   *  (e.g. rapid double-clicks of Start) so we don't init Tone twice or
   *  start two parallel sample-loads. */
  const ensureAudioPromiseRef = useRef<Promise<AudioPlayer> | null>(null);

  /** Resumable session loaded from IndexedDB on mount; banner offers
   * resume/discard. Cleared once the user picks one (or starts fresh). */
  const [resumable, setResumable] = useState<SessionRecord | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // ── Acquisition gate (PR 3) ──────────────────────────────────────────────
  type AcquisitionGate = "none" | "quiz-ready" | "quiz" | "quiz-pass" | "quiz-fail";
  const [acquisitionGate, setAcquisitionGate] = useState<AcquisitionGate>("none");
  const acquisitionGateDismissedRef = useRef(false);

  // Quiz sub-state
  const [quizItems, setQuizItems] = useState<Item[]>([]);
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizResults, setQuizResults] = useState<boolean[]>([]);
  const [quizExercise, setQuizExercise] = useState<Exercise | null>(null);
  type QuizPhase = "playing" | "awaiting" | "graded";
  const [quizPhase, setQuizPhase] = useState<QuizPhase>("playing");
  const [quizSelected, setQuizSelected] = useState<string | null>(null);
  const [quizTimer, setQuizTimer] = useState(8);

  const cards = useCardsStore((s) => s.cards);
  const applyReview = useCardsStore((s) => s.applyReview);
  const seedFromLesson = useCardsStore((s) => s.seedFromLesson);
  const includeLeadingTone = usePrefsStore((s) => s.includeLeadingTone);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);
  const unlockEverything = usePrefsStore((s) => s.unlockEverything);
  const instrument = usePrefsStore((s) => s.instrument);
  const vocalModeEnabled = usePrefsStore((s) => s.vocalModeEnabled);
  const midiInputEnabled = usePrefsStore((s) => s.midiInputEnabled);
  const midiSelectedDeviceId = usePrefsStore((s) => s.midiSelectedDeviceId);
  const sessionTargetMinutes = usePrefsStore((s) => s.sessionTargetMinutes);
  const sessionMix = usePrefsStore((s) => s.sessionMix);
  const newSkillNodeId = usePrefsStore((s) => s.newSkillNodeId);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);
  const showNextReviewHint = usePrefsStore((s) => s.showNextReviewHint);
  const confidenceSelfReport = usePrefsStore((s) => s.confidenceSelfReport);

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { preSeededItems?: Item[]; planStops?: PlanStop[]; bypassQuiz?: boolean } | null;
  // planStops comes from router state on a fresh session start, or from the
  // persisted SessionRecord when resuming (location.state is gone after a reload).
  const [resumedPlanStops, setResumedPlanStops] = useState<PlanStop[] | null>(null);
  const planStops = resumedPlanStops ?? locationState?.planStops ?? null;

  // Introduce stop + gate-eligible nodes (derived once from planStops).
  const introduceStop = useMemo(
    () => planStops?.find((s) => s.kind === "introduce") ?? null,
    [planStops],
  );
  const introduceItemIds = useMemo(
    () => new Set((introduceStop?.items ?? []).map((i) => i.id)),
    [introduceStop],
  );
  const gateNodes = useMemo(
    () => introduceStop?.nodeAcquisition?.filter((a) => a.status === "needs-listen") ?? [],
    [introduceStop],
  );
  const primaryGateNode = gateNodes[0] ?? null;
  const primaryGateNodeInfo = useMemo(
    () => (primaryGateNode ? (MVP_NODES.find((n) => n.id === primaryGateNode.nodeId) ?? null) : null),
    [primaryGateNode],
  );

  const currentStopIndex = useMemo(() => {
    if (!planStops || planStops.length === 0) return 0;
    let cumulative = 0;
    for (let i = 0; i < planStops.length; i++) {
      cumulative += planStops[i]!.itemCount;
      if (session.itemsReviewed < cumulative) return i;
    }
    return planStops.length - 1;
  }, [planStops, session.itemsReviewed]);

  const sessionConfig = useMemo(
    () => buildSessionConfig(sessionTargetMinutes, sessionMix),
    [sessionTargetMinutes, sessionMix],
  );

  /** Preview the plan we'd build *right now* so the UI can show "no items
   * due" instead of letting the user click Start and silently get nothing.
   * Recomputed on focus/pref changes; cheap (filter + scheduler over ~27 items). */
  const previewPlan = useMemo(
    () =>
      buildSession({
        items: filterByPrefs(buildAllItems(), { includeLeadingTone }),
        cards,
        now: new Date(),
        forceAllUnlocked: unlockEverything,
        config: sessionConfig,
        newSkillNodeId,
        thresholds: MASTERY_PRESETS[masteryPreset],
      }),
    [cards, includeLeadingTone, unlockEverything, sessionConfig, newSkillNodeId, masteryPreset],
  );
  const noItemsDue = previewPlan.items.length === 0;

  /** Which scheduling bucket (review / new) each item in the current
   * session came from. Set when a session starts; cleared on session dismiss.
   * Used to label the category chip on the ExerciseCard header. */
  const [itemCategories, setItemCategories] = useState<Record<string, "review" | "new">>({});

  /** Set to the node that was just finished (last item from that node reviewed
   * this session). Drives the "node complete" milestone strip below ExerciseCard.
   * Cleared at the start of each new exercise so it doesn't persist across questions. */
  const [nodeJustCompleted, setNodeJustCompleted] = useState<{ id: string; title: string } | null>(null);

  /** Stable breakdown derived from itemCategories — holds the original planned
   * split throughout the session so the header line doesn't shift as card states
   * update mid-session. Falls back to previewPlan.breakdown when idle. */
  const sessionBreakdown = useMemo(() => {
    const cats = Object.values(itemCategories);
    if (cats.length === 0) return null;
    return {
      review: cats.filter((c) => c === "review").length,
      new: cats.filter((c) => c === "new").length,
    };
  }, [itemCategories]);

  const displayBreakdown = sessionBreakdown ?? (previewPlan.items.length > 0 ? previewPlan.breakdown : null);

  useEffect(() => {
    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
      uiSoundRef.current?.destroy();
      uiSoundRef.current = null;
      midiRef.current?.destroy();
      midiRef.current = null;
    };
  }, []);

  // Tear down the MIDI runtime when the user disables MIDI mode while
  // Practice is mounted. Re-enabling spins up a fresh instance via the
  // listener-effect below.
  useEffect(() => {
    if (midiInputEnabled) return;
    midiRef.current?.destroy();
    midiRef.current = null;
    midiNotesRef.current = [];
    setMidiDevices([]);
  }, [midiInputEnabled]);

  // Reset the MIDI buffer + submit-guard at every exercise boundary.
  // Notes from the previous question must not bleed into the next one.
  useEffect(() => {
    midiNotesRef.current = [];
    midiSubmittedRef.current = false;
  }, [exercise?.itemId]);


  // Mid-session adaptive pacing: evaluated at every REBALANCE_INTERVAL-th review.
  // Gated behind DEV mode or unlockEverything so thresholds can be tuned before
  // shipping to all users (per phase-2d-spec §9).
  useEffect(() => {
    const rebalanceEnabled = import.meta.env.DEV || unlockEverything;
    if (!rebalanceEnabled) return;
    if (session.phase !== "running") return;
    if (session.itemsReviewed === 0) return;
    if (session.itemsReviewed % REBALANCE_INTERVAL !== 0) return;

    const { again, hard, good, easy } = session.ratingCounts;
    const total = again + hard + good + easy;
    if (total === 0) return;
    const againRate = again / total;

    const maxSessionSize = Math.round(sessionTargetMinutes * ITEMS_PER_MINUTE * 1.25);
    const currentTotal = session.itemsReviewed + session.queue.length;

    const allItems = filterByPrefs(buildAllItems(), { includeLeadingTone });
    const queueIds = new Set(session.queue.map((i) => i.id));

    if (againRate > REBALANCE_AGAIN_HIGH) {
      // Swap new-state items out; pull due-review items in instead.
      const newItemsInQueue = session.queue.filter(
        (item) => !cards[item.id] || cards[item.id]!.state === "new",
      );
      const toRemove = newItemsInQueue.slice(0, REBALANCE_MAX_SWAP);
      if (toRemove.length === 0) return;

      const freshPlan = buildSession({
        items: allItems, cards, now: new Date(),
        forceAllUnlocked: unlockEverything, config: sessionConfig,
        newSkillNodeId, thresholds: MASTERY_PRESETS[masteryPreset],
      });
      const reviewItems = freshPlan.items.filter(
        (item) =>
          (cards[item.id]?.state === "review" || cards[item.id]?.state === "relearning") &&
          !queueIds.has(item.id),
      );
      const toAdd = reviewItems.slice(0, toRemove.length);

      sessionDispatch({
        type: "REBALANCE",
        removeItemIds: toRemove.map((i) => i.id),
        additionalItems: toAdd,
      });
    } else if (againRate < REBALANCE_AGAIN_LOW && session.queue.length < 5 && currentTotal < maxSessionSize) {
      // Queue is short and the user is breezing through — append one new item.
      const freshPlan = buildSession({
        items: allItems, cards, now: new Date(),
        forceAllUnlocked: unlockEverything, config: sessionConfig,
        newSkillNodeId, thresholds: MASTERY_PRESETS[masteryPreset],
      });
      const newItems = freshPlan.items.filter(
        (item) => (!cards[item.id] || cards[item.id]!.state === "new") && !queueIds.has(item.id),
      );
      if (newItems.length === 0) return;

      sessionDispatch({
        type: "REBALANCE",
        removeItemIds: [],
        additionalItems: [newItems[0]!],
      });
    }
    // Primary trigger is itemsReviewed; other values are read from closure at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.itemsReviewed]);

  useEffect(() => {
    if (!awaitingConfidence) return;
    if (confidenceCountdown <= 0) {
      confidenceResolveRef.current(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setConfidenceCountdown((c) => c - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [awaitingConfidence, confidenceCountdown]);

  // Quiz per-item countdown: auto-submits as wrong when it hits 0.
  useEffect(() => {
    if (acquisitionGate !== "quiz") return;
    if (quizPhase !== "awaiting") return;
    if (quizTimer <= 0) {
      quizAnswerRef.current("__timeout__");
      return;
    }
    const t = window.setTimeout(() => setQuizTimer((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [acquisitionGate, quizPhase, quizTimer]);

  // When navigated here from "I know this", show a quiz-ready prompt.
  // We cannot auto-start audio here — Web Audio requires a user gesture, which
  // is lost by the time this effect fires. The user clicks "Start quiz" instead.
  useEffect(() => {
    if (!locationState?.bypassQuiz) return;
    if (gateNodes.length === 0) return;
    if (acquisitionGateDismissedRef.current) return;
    acquisitionGateDismissedRef.current = true;
    setAcquisitionGate("quiz-ready");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** MIDI subscriber prop for <DictationInput>. Provides the same
   *  noteon stream Practice uses for the recognition graders, exposed
   *  as a registration function so the UI component never imports Web
   *  MIDI directly (architecture invariant: Web MIDI stays at the
   *  apps/web boundary). Only wires up when MIDI mode is on AND a
   *  `MidiInput` has been initialized — i.e. the user has already
   *  granted permission via the settings toggle. */
  const dictationMidiSubscriber = useMemo(() => {
    if (!midiInputEnabled) return undefined;
    return (handler: (midi: number) => void) => {
      const m = midiRef.current;
      if (!m) return () => {};
      return m.onNoteOn((event) => {
        handler(event.midi);
      });
    };
  }, [midiInputEnabled]);

  /**
   * Subscribe to MIDI noteon events while a supported exercise is
   * `awaiting` (post-playback, pre-answer). On every noteon we push
   * into the buffer and run the grader — when the grader returns a
   * match, we auto-submit through the same `handleAnswer` path that
   * clicking goes through, so MIDI answers go through FSRS, the chime,
   * and teach playback unchanged.
   *
   * The MidiInput is lazy-initialized inside this effect so a Practice
   * mount with MIDI mode off never touches navigator.requestMIDIAccess.
   * For users who enabled MIDI in Settings, init() is silent because
   * permission has already been granted to this origin.
   */
  useEffect(() => {
    if (!midiInputEnabled) return;
    if (!exercise) return;
    if (phase !== "awaiting") return;
    if (!isMidiSupportedExercise(exercise.exerciseType)) return;

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    let unsubscribeDevices: (() => void) | null = null;

    void (async () => {
      let m = midiRef.current;
      if (!m) {
        m = createMidiInput();
        try {
          await m.init();
        } catch {
          // Permission was denied or the device is unavailable. Don't
          // surface an error from Practice — Settings owns the
          // permission UX. Silent fallback to clicking.
          m.destroy();
          return;
        }
        if (cancelled) {
          m.destroy();
          return;
        }
        midiRef.current = m;
        m.selectDevice(midiSelectedDeviceId);
        setMidiDevices(m.listDevices());
        unsubscribeDevices = m.onDevicesChanged((devices) => {
          if (!cancelled) setMidiDevices(devices);
        });
      } else {
        // Re-mounting onto an existing MidiInput: refresh the device
        // selection in case the picker changed since the last
        // exercise.
        m.selectDevice(midiSelectedDeviceId);
        setMidiDevices(m.listDevices());
      }

      unsubscribe = m.onNoteOn((event) => {
        if (cancelled) return;
        if (midiSubmittedRef.current) return;
        midiNotesRef.current.push(event);
        const result = gradeMidiAnswer(midiNotesRef.current, exercise);
        if (!result) return;
        midiSubmittedRef.current = true;
        handleSubmit({ type: "option", optionId: result.optionId });
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      if (unsubscribeDevices) unsubscribeDevices();
    };
    // handleAnswer is a stable closure for the duration of an exercise;
    // refs cover the rest. The deps below are exactly the gates that
    // determine whether we should be subscribed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midiInputEnabled, exercise, phase, midiSelectedDeviceId]);

  /** On mount, look for an unfinished session. Show the resume banner only
   * when the persisted queue still has items — otherwise the record is a
   * leftover from before queue persistence shipped, or a finished session
   * with a pending writeback, and there's nothing useful to resume. */
  useEffect(() => {
    let cancelled = false;
    void dbGetActiveSession().then((found) => {
      if (cancelled) return;
      if (found && found.queue && found.queue.length > 0) {
        setResumable(found);
      } else if (found) {
        // Stale "active" record with no queue — close it so the next session
        // doesn't trip over the same orphan on the next mount.
        void dbEndSession(found.id, {
          itemsReviewed: found.itemsReviewed,
          correctCount: found.correctCount,
          averageResponseMs: found.averageResponseMs,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Initialize the audio engine and load the user's chosen instrument
   * before returning. Tone.start() requires a user gesture, so this
   * runs from the Start-exercise click rather than on mount.
   *
   * Earlier behavior fired loadInstrument off in the background and
   * returned immediately — that made the first exercise play on the
   * synth even when the user had selected piano. Now the load is
   * awaited so the first audible note matches the pref.
   *
   * Concurrent callers (e.g. rapid double-click of Start) share the
   * same promise via ensureAudioPromiseRef, so init runs once.
   */
  const ensureAudio = (): Promise<AudioPlayer> => {
    if (playerRef.current) return Promise.resolve(playerRef.current);
    if (ensureAudioPromiseRef.current) return ensureAudioPromiseRef.current;

    const promise = (async () => {
      setAudioInitiating(true);
      try {
        const player = createAudioPlayer();
        await player.init();
        playerRef.current = player;

        // Synth is built into the engine, no samples to fetch.
        if (instrument === "synth") {
          setAudioStatus("ready");
          setPianoProgress(undefined);
          return player;
        }

        setAudioStatus("loading");
        setPianoProgress(0);
        try {
          await player.loadInstrument(instrument, {
            onProgress: (loaded, total) => {
              setPianoProgress(total > 0 ? loaded / total : 0);
            },
          });
          try {
            player.setInstrument(instrument);
            setAudioStatus("ready");
          } catch {
            // loadInstrument silently no-ops for instruments without
            // samples (currently guitar/voice). The active instrument
            // stays "synth"; surface that honestly.
            setAudioStatus("synth-fallback");
          }
        } catch {
          setAudioStatus("synth-fallback");
        } finally {
          setPianoProgress(undefined);
        }
        return player;
      } finally {
        setAudioInitiating(false);
      }
    })();

    ensureAudioPromiseRef.current = promise;
    promise.finally(() => {
      ensureAudioPromiseRef.current = null;
    });
    return promise;
  };

  const handleNoteStart = useCallback((event: { midi: number; voice?: string }) => {
    setActiveMidis((prev) => {
      const next = new Set(prev);
      next.add(event.midi);
      return next;
    });
    if (event.voice === "bass") {
      const idx = bassIndexRef.current;
      bassIndexRef.current = idx + 1;
      setActivePip(idx);
    }
  }, []);

  const handleNoteEnd = useCallback((event: { midi: number; voice?: string }) => {
    setActiveMidis((prev) => {
      const next = new Set(prev);
      next.delete(event.midi);
      return next;
    });
    if (event.voice === "bass") {
      setActivePip(null);
      setDonePips((d) => [...d, d.length]);
    }
  }, []);

  const playCurrent = async (player: AudioPlayer, audio: AudioPlan) => {
    setActiveMidis(new Set());
    setDonePips([]);
    setActivePip(null);
    bassIndexRef.current = 0;
    await player.play(audio, {
      onNoteStart: handleNoteStart,
      onNoteEnd: handleNoteEnd,
    });
    setActivePip(null);
    setActiveMidis(new Set());
  };

  /**
   * Drive the next click of "Start exercise" / "Next question". Three paths:
   *   - No active session → create one in the DB, build a fresh queue, drill the head.
   *   - Active session, queue non-empty → drill the head.
   *   - Active session, queue empty → close the session out and show the summary.
   */
  const startExercise = async () => {
    // Acquisition gate: redirect to Listen mode before the first Introduce item
    // when the exposure threshold isn't met. Only fires before the session starts.
    if (!acquisitionGateDismissedRef.current && !isSessionActive(session)) {
      const seededItems = locationState?.preSeededItems ?? [];
      const nextItem = seededItems.length > 0 ? seededItems[0] : null;
      if (
        planStops &&
        nextItem &&
        introduceItemIds.has(nextItem.id) &&
        gateNodes.length > 0
      ) {
        acquisitionGateDismissedRef.current = true;
        const listenRoute = primaryGateNode ? nodeToListenRoute(primaryGateNode.nodeId) : null;
        if (listenRoute) {
          navigate(listenRoute, { replace: true });
          return;
        }
        // No listen route (unusual) — fall through and let the session start
      }
    }

    const player = await ensureAudio();

    let queue: Item[];
    let sid: string;

    if (!isSessionActive(session)) {
      // Pre-seeded queue (e.g. "Weakest items" CTA on Home) takes precedence
      // over the normal buildSession() path.
      const seeded = locationState?.preSeededItems;
      let sessionItems: Item[];
      if (seeded && seeded.length > 0) {
        sessionItems = seeded;
        setItemCategories(Object.fromEntries(seeded.map((i) => [i.id, "review" as const])));
      } else {
        const allItems = filterByPrefs(buildAllItems(), { includeLeadingTone });
        const builtPlan = buildSession({
          items: allItems,
          cards,
          now: new Date(),
          forceAllUnlocked: unlockEverything,
          config: sessionConfig,
          newSkillNodeId,
          thresholds: MASTERY_PRESETS[masteryPreset],
          // Fresh per-session seed so a new session doesn't replay the previous
          // session's item order on the same day. Preview (above) keeps the
          // day-seed so it stays stable across re-renders.
          seed: Date.now(),
        });
        if (builtPlan.items.length === 0) {
          // No items available — leave the page in `ready` (rare; locked + empty curriculum).
          return;
        }
        sessionItems = builtPlan.items;
        setItemCategories(builtPlan.itemCategories);
      }
      sid = await dbStartSession(sessionItems, locationState?.planStops);
      const atIso = new Date().toISOString();
      sessionDispatch({
        type: "SESSION_STARTED",
        id: sid,
        queue: sessionItems,
        atPerf: performance.now(),
        atIso,
      });
      queue = sessionItems;
    } else {
      sid = session.sessionId!;
      queue = session.queue;
      if (queue.length === 0) {
        // The previous answer was the last item — close out and show summary.
        const stats = sessionStats(session);
        await dbEndSession(sid, stats);
        sessionDispatch({ type: "SESSION_ENDED" });
        return;
      }
    }

    const head = queue[0];
    if (!head) return;
    sessionDispatch({ type: "ITEM_CONSUMED" });
    setNodeJustCompleted(null);

    const ex = generate(head, Date.now(), { cadenceTempo });
    practiceDispatch({ type: "EXERCISE_GENERATED", exercise: ex });
    await playCurrent(player, ex.audio);
    practiceDispatch({ type: "PLAYBACK_FINISHED", at: performance.now() });
  };

  const replayAudio = async () => {
    if (!exercise || !playerRef.current) return;
    practiceDispatch({ type: "REPLAY_STARTED" });
    await playCurrent(playerRef.current, exercise.audio);
    practiceDispatch({ type: "PLAYBACK_FINISHED", at: performance.now() });
  };

  /**
   * Shared post-grade pipeline: chime + FSRS review + session update +
   * teach-audio playback for the wrong-answer case. Both `handleAnswer`
   * (recognition exercises) and `handleDictationSubmit` route through
   * this so the side effects stay identical.
   */
  const finalizeReview = (
    ex: Exercise,
    correct: boolean,
    rating: Rating,
    responseMs: number,
    chosenAnswerId: string | null = null,
  ) => {
    if (uiSoundsEnabled) {
      if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
      uiSoundRef.current.play(correct ? "correct" : "wrong");
    }

    void applyReview(ex.itemId, rating);

    if (!correct && ex.meta.teachAudio && playerRef.current) {
      const teachPlan = ex.meta.teachAudio;
      const player = playerRef.current;
      window.setTimeout(() => {
        void playCurrent(player, teachPlan);
      }, 600);
    }

    if (session.sessionId) {
      const sid = session.sessionId;
      void dbRecordReview({
        itemId: ex.itemId,
        sessionId: sid,
        rating,
        correct,
        responseMs,
        chosenAnswerId: !correct ? chosenAnswerId : null,
        source: "practice",
      });
      sessionDispatch({ type: "REVIEW_RECORDED", correct, responseMs, rating });
      const itemsReviewed = session.itemsReviewed + 1;
      const correctCount = session.correctCount + (correct ? 1 : 0);
      const responseMsTotal = session.responseMsTotal + responseMs;
      void dbUpdateSessionAfterReview(sid, session.queue, {
        itemsReviewed,
        correctCount,
        averageResponseMs: itemsReviewed > 0 ? responseMsTotal / itemsReviewed : 0,
      });
    }
  };

  const handleConfidenceResolve = (unsure: boolean) => {
    const pending = pendingConfidenceRef.current;
    if (!pending) return;
    pendingConfidenceRef.current = null;
    setAwaitingConfidence(false);
    setConfidenceCountdown(0);
    const resolvedRating: Rating = unsure ? "hard" : pending.rating;
    finalizeReview(pending.ex, pending.correct, resolvedRating, pending.responseMs, pending.chosenAnswerId);
  };
  confidenceResolveRef.current = handleConfidenceResolve;

  /** Fires the node-completion milestone when the just-answered exercise was
   * the last item from its curriculum node remaining in the session queue. */
  const markNodeCompletionIfLast = (ex: Exercise) => {
    const allItems = buildAllItems();
    const item = allItems.find((i) => i.id === ex.itemId);
    if (!item) return;
    if (!session.queue.some((qi) => qi.nodeId === item.nodeId)) {
      const node = MVP_NODES.find((n) => n.id === item.nodeId);
      if (node) setNodeJustCompleted({ id: node.id, title: node.title });
    }
  };

  const handleSubmit = (answer: ExerciseAnswer) => {
    if (!exercise || !canAnswer(practice)) return;
    const result = gradeExercise(exercise, answer);
    const responseMs = performance.now() - practice.awaitingStartedAt;
    const rating = deriveRating(result, answer, responseMs);

    practiceDispatch({ type: "ANSWER_SUBMITTED", answer, result });
    markNodeCompletionIfLast(exercise);

    const chosenAnswerId = !result.correct && answer.type === "option" ? answer.optionId : null;
    if (result.correct && confidenceSelfReport && exercise.exerciseType !== "melodic-dictation") {
      pendingConfidenceRef.current = { ex: exercise, correct: result.correct, rating, responseMs, chosenAnswerId };
      setAwaitingConfidence(true);
      setConfidenceCountdown(2);
    } else {
      finalizeReview(exercise, result.correct, rating, responseMs, chosenAnswerId);
    }
  };

  const handlePianoKeyClick = (midi: number) => {
    if (!exercise) return;

    playerRef.current?.playNote(midi, 0.5);

    setActiveMidis((prev) => new Set([...prev, midi]));
    window.setTimeout(() => {
      setActiveMidis((prev) => {
        const next = new Set(prev);
        next.delete(midi);
        return next;
      });
    }, 400);

    if (exercise.exerciseType === "scale-degree-recognition") {
      const { tonicMidi, tonalityMode } = exercise.meta;
      if (tonicMidi === undefined) return;
      const sd = midiToScaleDegree(midi, tonicMidi, tonalityMode ?? "major");
      if (!sd) return;
      handleSubmit({ type: "option", optionId: `degree-${sd.degree}` });
    } else if (exercise.exerciseType === "melodic-dictation" && phase === "awaiting") {
      const { tonicMidi, tonalityMode, chromaticEnabled } = exercise.meta;
      if (tonicMidi === undefined) return;
      const sd = midiToScaleDegree(midi, tonicMidi, tonalityMode ?? "major", {
        allowChromatic: chromaticEnabled ?? false,
      });
      if (!sd) return;
      const currentSeq = practice.pendingAnswer?.type === "dictation" ? practice.pendingAnswer.sequence : [];
      practiceDispatch({ type: "ANSWER_CHANGE", answer: { type: "dictation", sequence: [...currentSeq, sd] } });
    }
  };

  /**
   * Keyboard shortcuts. Built fresh each render so handlers see the
   * latest `practice` and `exercise` state — the hook reads from a ref
   * so this doesn't re-bind the listener.
   *
   *   Space  start / next question (whichever the primary button is doing)
   *   R      replay audio (when allowed)
   *   N      next question (when graded)
   *   1..9   pick the Nth answer option (recognition exercises)
   *   1..7   enter solfège degree (dictation)
   *   Backspace  undo last entered note (dictation)
   *   Enter      submit (dictation, when sequence non-empty)
   */
  const shortcuts = useMemo<ShortcutMap>(() => {
    const map: ShortcutMap = {};
    const primaryReady = phase === "ready" && !resumable && !(noItemsDue && !isSessionActive(session));
    const primaryGraded = phase === "graded" && !awaitingConfidence;
    if (primaryReady || primaryGraded) {
      map.space = (e) => {
        e.preventDefault();
        void startExercise();
      };
      map.n = () => {
        if (primaryGraded) void startExercise();
      };
    }
    if (canReplay(practice)) {
      map.r = () => {
        void replayAudio();
      };
    }
    if (exercise && canAnswer(practice)) {
      if (exercise.exerciseType === "rhythmic-dictation") {
        map.enter = (e) => {
          e.preventDefault();
          if (practice.pendingAnswer?.type === "rhythmic") {
            handleSubmit(practice.pendingAnswer);
          }
        };
      } else if (exercise.exerciseType === "melodic-dictation") {
        // Number keys 1..7 enter the corresponding solfège degree.
        // Backspace = undo last entry; Enter = submit when non-empty.
        for (let i = 1; i <= 7; i++) {
          map[String(i)] = () => {
            const currentSeq = practice.pendingAnswer?.type === "dictation" ? practice.pendingAnswer.sequence : [];
            practiceDispatch({ type: "ANSWER_CHANGE", answer: { type: "dictation", sequence: [...currentSeq, { degree: i }] } });
          };
        }
        map.backspace = (e) => {
          e.preventDefault();
          const currentSeq = practice.pendingAnswer?.type === "dictation" ? practice.pendingAnswer.sequence : [];
          practiceDispatch({ type: "ANSWER_CHANGE", answer: { type: "dictation", sequence: currentSeq.slice(0, -1) } });
        };
        map.enter = (e) => {
          e.preventDefault();
          if (practice.pendingAnswer?.type === "dictation" && practice.pendingAnswer.sequence.length > 0) {
            handleSubmit(practice.pendingAnswer);
          }
        };
      } else {
        exercise.options.forEach((opt, i) => {
          map[String(i + 1)] = () => handleSubmit({ type: "option", optionId: opt.id });
        });
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, exercise, practice, resumable, noItemsDue, session, awaitingConfidence]);

  useKeyboardShortcuts(shortcuts);

  const handleSessionContinue = () => {
    sessionDispatch({ type: "SESSION_DISMISSED" });
    void startExercise();
  };

  const handleSessionReturnHome = () => {
    sessionDispatch({ type: "SESSION_DISMISSED" });
    navigate("/review");
  };

  const handleResume = () => {
    if (!resumable || !resumable.queue) return;
    const itemsReviewed = resumable.itemsReviewed;
    sessionDispatch({
      type: "SESSION_RESUMED",
      id: resumable.id,
      queue: resumable.queue,
      atPerf: performance.now(),
      atIso: resumable.startedAt,
      itemsReviewed,
      correctCount: resumable.correctCount,
      // We didn't persist responseMsTotal, so reconstruct it from the
      // average. Slight rounding error vs the live total, but the running
      // average stays correct after this point because we recompute it
      // from the running total + new reviews.
      responseMsTotal: Math.round(resumable.averageResponseMs * itemsReviewed),
    });
    setResumedPlanStops(resumable.planStops ?? null);
    setResumable(null);
  };

  const handleDiscardResumable = () => {
    if (!resumable) return;
    void dbEndSession(resumable.id, {
      itemsReviewed: resumable.itemsReviewed,
      correctCount: resumable.correctCount,
      averageResponseMs: resumable.averageResponseMs,
    });
    setResumable(null);
  };

  // ── Acquisition gate handlers ──────────────────────────────────────────────

  const handleQuizAnswer = (optionId: string) => {
    if (!quizExercise || quizPhase !== "awaiting") return;
    const correct = optionId !== "__timeout__" && optionId === quizExercise.correctAnswerId;
    setQuizSelected(optionId === "__timeout__" ? quizExercise.correctAnswerId : optionId);
    setQuizPhase("graded");

    const newResults = [...quizResults, correct];
    setQuizResults(newResults);

    const isLast = quizIdx + 1 >= quizItems.length;
    if (isLast) {
      const correctCount = newResults.filter(Boolean).length;
      if (correctCount >= Math.round(newResults.length * 0.8)) {
        setAcquisitionGate("quiz-pass");
        // Seed FSRS for quiz items based on quiz results, remaining items via seedFromLesson.
        void (async () => {
          for (let i = 0; i < quizItems.length; i++) {
            const item = quizItems[i];
            if (item) await applyReview(item.id, newResults[i] ? "good" : "again");
          }
          const quizItemIds = new Set(quizItems.map((it) => it.id));
          for (const item of (introduceStop?.items ?? [])) {
            if (!quizItemIds.has(item.id)) await seedFromLesson(item.id);
          }
          if (primaryGateNode) {
            await recordBypassQuizPassed(primaryGateNode.nodeId, primaryGateNode.threshold);
          }
        })();
      } else {
        setAcquisitionGate("quiz-fail");
      }
    }
  };
  quizAnswerRef.current = handleQuizAnswer;

  const playQuizItem = async (player: AudioPlayer, item: Item, itemTimer = 8) => {
    setQuizPhase("playing");
    setQuizSelected(null);
    setQuizTimer(itemTimer);
    const ex = generate(item, Date.now(), { cadenceTempo });
    setQuizExercise(ex);
    await playCurrent(player, ex.audio);
    setQuizTimer(8);
    setQuizPhase("awaiting");
  };

  const startBypassQuiz = async () => {
    const player = await ensureAudio();
    // Exclude dictation and fret exercises — they use grid/fretboard input, not multiple choice.
    const eligible = (introduceStop?.items ?? []).filter(
      (item) =>
        !item.nodeId.startsWith("melodic-dictation") &&
        !item.nodeId.startsWith("rhythmic-dictation") &&
        !item.nodeId.startsWith("fret-"),
    );
    if (eligible.length === 0) return;
    // Scale quiz length with the node's acquisition threshold — harder skills
    // require more evidence before the bypass is credited (max 20 items).
    const threshold = primaryGateNode?.threshold ?? 20;
    const TARGET = Math.min(20, Math.max(10, Math.round(threshold / 4)));
    const picked: Item[] = [];
    let pool = [...eligible].sort(() => Math.random() - 0.5);
    while (picked.length < TARGET) {
      for (const item of pool) {
        if (picked.length >= TARGET) break;
        picked.push(item);
      }
      pool = [...eligible].sort(() => Math.random() - 0.5);
    }
    setQuizItems(picked);
    setQuizIdx(0);
    setQuizResults([]);
    setQuizExercise(null);
    setAcquisitionGate("quiz");
    await playQuizItem(player, picked[0]!);
  };

  const advanceQuiz = async () => {
    const player = playerRef.current;
    if (!player || quizPhase !== "graded") return;
    const nextIdx = quizIdx + 1;
    setQuizIdx(nextIdx);
    const item = quizItems[nextIdx];
    if (!item) return;
    await playQuizItem(player, item);
  };

  // Re-runs startBypassQuiz so the pool is reshuffled into a fresh 10-item
  // sequence. Reusing the old quizItems array would give the same item order
  // every retry, letting the user memorise the sequence rather than the skill.
  const retryBypassQuiz = () => void startBypassQuiz();

  if (session.phase === "summary") {
    const durationSeconds = Math.max(
      0,
      Math.round((performance.now() - session.startedAtPerf) / 1000),
    );
    return (
      <div className="max-w-2xl mx-auto pt-8">
        <SessionSummary
          itemsReviewed={session.itemsReviewed}
          correctCount={session.correctCount}
          durationSeconds={durationSeconds}
          ratingCounts={session.ratingCounts}
          onContinue={handleSessionContinue}
          onReturnHome={handleSessionReturnHome}
          onFeedback={() => setFeedbackOpen(true)}
        />
        {feedbackOpen && (
          <FeedbackModal
            screen="/review/session"
            onClose={() => setFeedbackOpen(false)}
            activeSkill={planStops?.[0]?.items[0]?.nodeId}
            sessionAccuracy={session.itemsReviewed > 0 ? session.correctCount / session.itemsReviewed : undefined}
          />
        )}
      </div>
    );
  }

  const sessionSubhead = !planStops && !resumable && displayBreakdown
    ? [
        displayBreakdown.review > 0 ? `${displayBreakdown.review} due` : null,
        displayBreakdown.new > 0 ? `${displayBreakdown.new} new` : null,
      ]
        .filter((s): s is string => s !== null)
        .join(" · ")
    : undefined;

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        back={{
          label: "← Today",
          href: "/review",
          onClick: () => { playerRef.current?.stop(); },
        }}
        eyebrow="session in progress"
        title={planStops || resumable ? "Session" : "Practice"}
        subhead={sessionSubhead}
        right={
          <>
            {midiInputEnabled && (
              <MidiStatusIndicator
                devices={midiDevices}
                selectedDeviceId={midiSelectedDeviceId}
              />
            )}
            <AudioStatusIndicator
              status={audioStatus}
              progress={pianoProgress}
              instrumentLabel={
                audioStatus === "ready"
                  ? instrument
                  : audioStatus === "synth-fallback"
                    ? "synth"
                    : undefined
              }
            />
          </>
        }
      />

      {planStops ? (
        <PlanBreadcrumb
          stops={planStops}
          currentStopIndex={currentStopIndex}
          itemsReviewed={session.itemsReviewed}
          nodes={MVP_NODES}
          cards={cards}
          quizOverride={
            (acquisitionGate === "quiz" || acquisitionGate === "quiz-pass" || acquisitionGate === "quiz-fail") && quizItems.length > 0
              ? { done: quizResults.length, total: quizItems.length }
              : undefined
          }
        />
      ) : null}

      {resumable && session.phase === "idle" && (
        <ResumeBanner record={resumable} onResume={handleResume} onDiscard={handleDiscardResumable} />
      )}

      {!resumable && session.phase === "idle" && previewPlan.newlyIntroducedNodeIds.length > 0 && acquisitionGate === "none" && (
        <IntroducingBanner nodeIds={previewPlan.newlyIntroducedNodeIds} />
      )}

      {!resumable && session.phase === "idle" && noItemsDue && acquisitionGate === "none" && (
        <div
          className="bg-paperWarm border border-ink/20 p-4 mb-4"
          role="region"
          aria-label="Nothing due"
        >
          <p className="font-mono text-[10px] uppercase tracking-widest text-success mb-1">
            all caught up
          </p>
          <p className="font-serif italic text-ink">
            Nothing due in this focus right now. Switch focus above, or come back tomorrow when reviews are ready.
          </p>
        </div>
      )}

      {/* Acquisition gate — quiz-ready prompt (user gesture needed to unlock audio) */}
      {acquisitionGate === "quiz-ready" && (
        <QuizReadyCard
          nodeInfo={primaryGateNodeInfo}
          onStart={() => void startBypassQuiz()}
        />
      )}

      {/* Acquisition gate — bypass quiz */}
      {acquisitionGate !== "none" && acquisitionGate !== "quiz-ready" && (
        <AcquisitionGateSection
          gate={acquisitionGate}
          primaryGateNode={primaryGateNode}
          nodeInfo={primaryGateNodeInfo}
          listenRoute={primaryGateNode ? nodeToListenRoute(primaryGateNode.nodeId) : null}
          quizItems={quizItems}
          quizIdx={quizIdx}
          quizResults={quizResults}
          quizExercise={quizExercise}
          quizPhase={quizPhase}
          quizSelected={quizSelected}
          quizTimer={quizTimer}
          onQuizAnswer={handleQuizAnswer}
          onAdvanceQuiz={() => void advanceQuiz()}
          onRetry={() => void retryBypassQuiz()}
          onPracticeAnyway={() => setAcquisitionGate("none")}
          onReturnToToday={() => navigate("/review")}
        />
      )}

      {acquisitionGate === "none" && (
      <><ExerciseCard
        context={
          exercise ? (
            <>
              Key: <strong className="font-serif italic text-base text-ink">{exercise.meta.keyName ?? "—"}</strong>
            </>
          ) : (
            "press start"
          )
        }
        status={(() => {
          if (!isSessionActive(session)) return `${counts.total} answered`;
          const counter = `${session.itemsReviewed + (phase === "playing" || phase === "awaiting" ? 1 : 0)} of ${session.totalItems}`;
          const category = exercise ? itemCategories[exercise.itemId] : undefined;
          if (!category) return counter;
          const categoryColors = { review: "text-warning", new: "text-success" } as const;
          return (
            <span className="flex items-center gap-2">
              <span className={categoryColors[category]}>{category}</span>
              {counter}
            </span>
          );
        })()}
        prompt={exercise?.prompt ?? "Press start to begin."}
        stagePips={
          phase === "playing" ? <StagePips count={CADENCE_PIP_COUNT} active={activePip} done={donePips} /> : undefined
        }
        controls={
          <div className="flex gap-2 flex-wrap">
            {phase === "ready" && !resumable && (
              <button
                type="button"
                onClick={() => void startExercise()}
                disabled={
                  audioStatus === "error" ||
                  audioInitiating ||
                  (noItemsDue && !isSessionActive(session))
                }
                className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {audioInitiating
                  ? pianoProgress !== undefined
                    ? `Loading samples… ${Math.round(pianoProgress * 100)}%`
                    : "Loading samples…"
                  : "▶ Start exercise"}
              </button>
            )}
            {phase === "graded" && !awaitingConfidence && (
              <button
                type="button"
                onClick={() => void startExercise()}
                className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
              >
                {session.queue.length === 0 ? "Finish →" : "Next →"}
              </button>
            )}
            {canReplay(practice) && (
              <button
                type="button"
                onClick={() => void replayAudio()}
                className="bg-transparent border border-ink/25 px-[18px] py-3 font-sans text-sm font-medium text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                ↻ Replay
              </button>
            )}
          </div>
        }
        options={
          <>
            {exercise && getRenderer(exercise.exerciseType)({
              exercise,
              pendingAnswer: practice.pendingAnswer,
              gradingResult: practice.gradingResult,
              disabled: !canAnswer(practice),
              onAnswerChange: (answer) => practiceDispatch({ type: "ANSWER_CHANGE", answer }),
              onSubmit: handleSubmit,
              midiSubscriber: dictationMidiSubscriber,
            })}
            {vocalModeEnabled &&
              exercise &&
              exercise.exerciseType === "scale-degree-recognition" && (
                <VocalSinger
                  exercise={exercise}
                  audioPlayer={playerRef.current}
                  enabled={canAnswer(practice)}
                  onAnswer={(optionId) => handleSubmit({ type: "option", optionId })}
                />
              )}
            {midiInputEnabled &&
              exercise &&
              !isMidiSupportedExercise(exercise.exerciseType) && (
                <p className="mt-3 text-xs text-inkFaded font-mono" role="status">
                  MIDI input doesn&apos;t grade this exercise type — pick an
                  option above, or switch input mode in Settings.
                </p>
              )}
          </>
        }
        feedback={
          <FeedbackBox
            state={
              phase === "graded"
                ? practice.gradingResult?.correct ? "correct" : "wrong"
                : "pending"
            }
          >
            {phase === "graded" && exercise && practice.gradingResult
              ? formatGradedFeedback(exercise, practice.pendingAnswer, practice.gradingResult)
              : phase === "playing"
                ? exercise?.exerciseType === "rhythmic-dictation"
                  ? "Listen to the count-in and the rhythm. Mark where the notes fall."
                  : exercise?.exerciseType === "melodic-dictation"
                  ? "Listen to the cadence, then the melody. Reproduce it on the input below."
                  : "Listen to the cadence, then identify what you heard."
                : phase === "awaiting"
                  ? exercise?.exerciseType === "rhythmic-dictation"
                    ? "Mark the beat cells where you heard notes, then submit."
                    : exercise?.exerciseType === "melodic-dictation"
                    ? "Enter the melody you heard, then submit."
                    : "Pick the answer that matches what you heard."
                  : "Press start to play a cadence and a target."}
            {phase === "graded" && showNextReviewHint && exercise && (() => {
              const card = cards[exercise.itemId];
              if (!card || card.state === "new") return null;
              const daysUntil = Math.round(
                (new Date(card.due).getTime() - Date.now()) / 86_400_000,
              );
              const nextLabel =
                daysUntil <= 0 ? "due now" : daysUntil === 1 ? "tomorrow" : `~${daysUntil} days`;
              const stabilityLabel =
                card.stability >= 1
                  ? `stability ${card.stability.toFixed(1)} d`
                  : "stability reset";
              return (
                <p className="font-mono text-xs text-inkFaded mt-2">
                  Next review: {nextLabel}  ·  {stabilityLabel}
                </p>
              );
            })()}
            {awaitingConfidence && (
              <div className="mt-3 flex flex-col items-start gap-2">
                <p className="font-mono text-xs text-inkFaded">Did that click?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleConfidenceResolve(false)}
                    className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
                    aria-label="Mark as got it — continue"
                  >
                    Got it
                  </button>
                  <button
                    type="button"
                    onClick={() => handleConfidenceResolve(true)}
                    className="bg-transparent border border-ink/25 px-[18px] py-3 font-sans text-sm font-medium text-ink hover:bg-ink hover:text-paper transition-colors"
                    aria-label="Mark as wasn't sure"
                  >
                    Wasn&apos;t sure
                  </button>
                </div>
                {confidenceCountdown > 0 && (
                  <p className="font-mono text-[10px] text-inkFaded" aria-live="polite">
                    auto-advance in {confidenceCountdown}s
                  </p>
                )}
              </div>
            )}
          </FeedbackBox>
        }
        footer={
          isSessionActive(session) ? (
            <p className="font-mono text-[10px] text-inkFaded">
              {counts.correct} correct of {counts.total}
            </p>
          ) : undefined
        }
      />

      {phase === "graded" && nodeJustCompleted && (
        <div className="mt-3 border border-success/30 bg-success/5 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-success mb-1">
            node complete
          </p>
          <p className="font-serif italic text-ink">
            {nodeJustCompleted.title} — all exercises reviewed for today ✓
          </p>
        </div>
      )}

      {exercise?.exerciseType === "melodic-dictation" && (
        <div className="mt-6">
          <PianoKeyboard
            fromMidi={48}
            toMidi={84}
            activeMidis={activeMidis}
            onKeyClick={phase === "awaiting" ? handlePianoKeyClick : undefined}
          />
        </div>
      )}
      </>
      )}

      <ShortcutsHint />

    </div>
  );
}

// ─── PlanBreadcrumb ───────────────────────────────────────────────────────────

const STOP_LEFT_RULE: Record<string, string> = {
  strengthen:  "border-l-4 border-l-warning",
  review:      "border-l-4 border-l-info",
  introduce:   "border-l-4 border-l-success",
  maintenance: "border-l-4 border-l-accent",
};
const STOP_DOT_BG: Record<string, string> = {
  strengthen:  "bg-warning",
  review:      "bg-info",
  introduce:   "bg-success",
  maintenance: "bg-accent",
};
const STOP_TAG: Record<string, string> = {
  strengthen:  "STRENGTHEN",
  review:      "REVIEW",
  introduce:   "INTRODUCE",
  maintenance: "KEEP SHARP",
};
const STOP_TAG_COLOR: Record<string, string> = {
  strengthen:  "text-warning",
  review:      "text-info",
  introduce:   "text-success",
  maintenance: "text-accent",
};

interface PlanBreadcrumbProps {
  stops: PlanStop[];
  currentStopIndex: number;
  itemsReviewed: number;
  nodes: CurriculumNode[];
  cards: CardStore;
  /** When set, overrides the done/total count for the introduce stop (quiz mode). */
  quizOverride?: { done: number; total: number };
}

function PlanBreadcrumb({ stops, currentStopIndex, itemsReviewed, nodes, cards, quizOverride }: PlanBreadcrumbProps): ReactNode {
  let cumulative = 0;
  return (
    <nav aria-label="Practice plan stops" className="flex gap-2 mb-5 flex-wrap">
      {stops.map((stop, i) => {
        const stopStart = cumulative;
        cumulative += stop.itemCount;
        const isDone = i < currentStopIndex;
        const isCurrent = i === currentStopIndex;

        const activeNodeChips: Array<{ title: string; lapseRate: number | null }> = [];
        if (isCurrent) {
          const seen = new Map<string, { title: string; lapseRate: number | null }>();
          for (const item of stop.items) {
            if (seen.has(item.nodeId)) continue;
            const title = nodes.find((n) => n.id === item.nodeId)?.title ?? item.nodeId;
            const nodeItems = stop.items.filter((it) => it.nodeId === item.nodeId);
            const reps = nodeItems.reduce((s, it) => s + (cards[it.id]?.reps ?? 0), 0);
            const lapses = nodeItems.reduce((s, it) => s + (cards[it.id]?.lapses ?? 0), 0);
            seen.set(item.nodeId, { title, lapseRate: reps > 0 ? lapses / reps : null });
          }
          activeNodeChips.push(...seen.values());
        }

        return (
          <div
            key={stop.kind + i}
            className={`flex-1 min-w-[120px] border border-ink/10 px-3 py-2 transition-opacity ${
              STOP_LEFT_RULE[stop.kind]
            } ${isCurrent ? "bg-paperWarm opacity-100" : isDone ? "bg-paper opacity-40" : "bg-paper opacity-50"}`}
            aria-current={isCurrent ? "step" : undefined}
            aria-label={`${STOP_TAG[stop.kind]}${isDone ? " — done" : isCurrent ? ` — ${quizOverride && stop.kind === "introduce" ? `${quizOverride.done} of ${quizOverride.total}` : `${Math.max(0, itemsReviewed - stopStart)} of ${stop.itemCount}`}` : ""}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              {isDone ? (
                <span className="w-2 h-2 rounded-full border border-ink/30 flex items-center justify-center" aria-hidden>
                  <span className="text-[6px] text-inkFaded leading-none">✓</span>
                </span>
              ) : (
                <span
                  className={`w-2 h-2 rounded-full ${STOP_DOT_BG[stop.kind]} ${isCurrent ? "animate-pulse" : "opacity-40"}`}
                  aria-hidden
                />
              )}
              <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${STOP_TAG_COLOR[stop.kind]}`}>
                {STOP_TAG[stop.kind]}
              </span>
            </div>
            {!isCurrent && (() => {
              const seen = new Set<string>();
              const titles: string[] = [];
              for (const item of stop.items) {
                if (seen.has(item.nodeId)) continue;
                seen.add(item.nodeId);
                titles.push(nodes.find((n) => n.id === item.nodeId)?.title ?? item.nodeId);
              }
              return titles.length > 0 ? (
                <p className="font-mono text-[10px] text-inkFaded leading-tight mt-0.5">{titles.join(" · ")}</p>
              ) : null;
            })()}
            {isCurrent && activeNodeChips.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {activeNodeChips.map(({ title, lapseRate }) => (
                  <li key={title} className="font-mono text-[10px] text-inkFaded leading-tight">
                    {title}
                    {lapseRate !== null && lapseRate > 0
                      ? ` · ${Math.round(lapseRate * 100)}% lapse`
                      : null}
                  </li>
                ))}
              </ul>
            )}
            {isCurrent && (
              <p className="font-mono text-[10px] text-inkFaded mt-1 tabular-nums">
                {quizOverride && stop.kind === "introduce"
                  ? `${quizOverride.done} of ${quizOverride.total}`
                  : `${Math.max(0, itemsReviewed - stopStart)} of ${stop.itemCount}`}
              </p>
            )}
          </div>
        );
      })}
    </nav>
  );
}


interface MidiStatusIndicatorProps {
  devices: MidiDeviceInfo[];
  selectedDeviceId: string | null;
}

/**
 * Compact "MIDI: <device-name>" badge shown in Practice's header when
 * MIDI mode is enabled. Mirrors the AudioStatusIndicator's visual
 * weight (mono caps, accent dot) so the two read as a pair without
 * crowding.
 */
function MidiStatusIndicator({
  devices,
  selectedDeviceId,
}: MidiStatusIndicatorProps): ReactNode {
  const connected = devices.filter((d) => d.state === "connected");
  // Pick the label: selected device if any, otherwise the first
  // connected device's name, otherwise "no device".
  let label: string;
  if (selectedDeviceId) {
    const match = devices.find((d) => d.id === selectedDeviceId);
    label = match ? match.name : "device offline";
  } else if (connected.length === 1) {
    label = connected[0]!.name;
  } else if (connected.length > 1) {
    label = `${connected.length} devices`;
  } else {
    label = "no device";
  }
  const live = connected.length > 0;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-widest font-mono border border-ink/15 bg-paper"
      aria-label={`MIDI input ${live ? "active" : "inactive"}: ${label}`}
    >
      <span
        aria-hidden
        className={`inline-block w-1.5 h-1.5 rounded-full ${
          live ? "bg-success" : "bg-ink/30"
        }`}
      />
      <span className="text-inkSoft">MIDI:</span>
      <span className="text-ink truncate max-w-[14ch]">{label}</span>
    </span>
  );
}

function ShortcutsHint(): ReactNode {
  return (
    <details className="mt-6 text-xs text-inkFaded font-mono">
      <summary className="cursor-pointer hover:text-ink select-none">
        keyboard shortcuts
      </summary>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 max-w-md">
        <dt><kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">Space</kbd></dt>
        <dd>start exercise · next question</dd>
        <dt><kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">N</kbd></dt>
        <dd>next question (when graded)</dd>
        <dt><kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">R</kbd></dt>
        <dd>replay audio</dd>
        <dt><kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">1</kbd>–<kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">9</kbd></dt>
        <dd>pick the Nth answer option (recognition) · enter the Nth solfège degree (dictation)</dd>
        <dt><kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">Backspace</kbd></dt>
        <dd>undo last entered note (dictation)</dd>
        <dt><kbd className="px-1.5 py-0.5 border border-ink/20 bg-paper">Enter</kbd></dt>
        <dd>submit (dictation)</dd>
      </dl>
    </details>
  );
}

interface ResumeBannerProps {
  record: SessionRecord;
  onResume: () => void;
  onDiscard: () => void;
}

function ResumeBanner({ record, onResume, onDiscard }: ResumeBannerProps): ReactNode {
  const itemsLeft = record.queue?.length ?? 0;
  const startedAt = new Date(record.startedAt);
  const formatter = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <div
      className="bg-paperWarm border border-ink/20 p-4 mb-4 flex items-center gap-3"
      role="region"
      aria-label="Unfinished session"
    >
      <div className="flex-1">
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink mb-1">Unfinished session</p>
        <p className="font-serif italic text-sm text-inkSoft">
          {record.itemsReviewed} reviewed · {itemsLeft} left · started {formatter.format(startedAt)}
        </p>
      </div>
      <button
        type="button"
        onClick={onResume}
        className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
      >
        Resume
      </button>
      <button
        type="button"
        onClick={onDiscard}
        className="bg-transparent border border-ink/25 px-[18px] py-3 font-sans text-sm font-medium text-ink hover:bg-ink hover:text-paper transition-colors"
      >
        Discard
      </button>
    </div>
  );
}

function deriveRating(result: GradingResult, answer: ExerciseAnswer, responseMs: number): Rating {
  if (answer.type === "dictation") {
    const detail = result.detail as DictationGrading | null | undefined;
    return result.correct ? "good" : dictationRatingFromAccuracy(detail?.accuracy ?? 0);
  }
  if (answer.type === "rhythmic") {
    const detail = result.detail as RhythmicDictationGrading | null | undefined;
    return rhythmicDictationRating(
      detail ?? { correct: false, cellStatuses: [], wrongCount: 1, accuracy: 0 },
    );
  }
  return ratingFromResponse(result.correct, responseMs);
}

function formatGradedFeedback(
  exercise: Exercise,
  answer: ExerciseAnswer | null,
  result: GradingResult,
): string {
  if (!result.correct && answer?.type === "option") {
    const wrongLabel = exercise.options.find((o) => o.id === answer.optionId)?.label ?? "?";
    const correctLabel = exercise.options.find((o) => o.id === exercise.correctAnswerId)?.label ?? "?";
    return `${wrongLabel} → ${correctLabel}. ${exercise.meta.explanation}`;
  }
  if (answer?.type === "rhythmic" && result.correct) {
    return exercise.meta.explanation;
  }
  return result.explanation;
}

// ─── Quiz-ready prompt ────────────────────────────────────────────────────────

function QuizReadyCard({
  nodeInfo,
  onStart,
}: {
  nodeInfo: { title: string } | null;
  onStart: () => void;
}): ReactNode {
  return (
    <div
      className="bg-paperWarm border border-ink/10 border-l-4 border-l-success p-6 mb-4"
      role="region"
      aria-label="Quick test ready"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-success mb-3">
        introduce · quick test
      </p>
      <p className="font-serif text-xl font-medium text-ink mb-1">
        {nodeInfo?.title ?? "This skill"}
      </p>
      <p className="font-serif italic text-sm text-inkSoft mb-6">
        10 questions — score 8 or more to unlock graded practice.
      </p>
      <button
        type="button"
        onClick={onStart}
        autoFocus
        className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
      >
        ▶ Start quiz
      </button>
    </div>
  );
}

// ─── Acquisition gate helpers + component ────────────────────────────────────

function nodeToListenRoute(nodeId: string): string | null {
  for (const [skill, nodeIds] of Object.entries(EXERCISE_SKILL_NODES)) {
    if ((nodeIds as readonly string[]).includes(nodeId)) {
      return `/exercise/${skill}?mode=listen&nodeId=${nodeId}`;
    }
  }
  return null;
}

interface AcquisitionGateSectionProps {
  gate: "quiz" | "quiz-pass" | "quiz-fail";
  primaryGateNode: { nodeId: string; exposures: number; threshold: number } | null;
  nodeInfo: { title: string } | null;
  listenRoute: string | null;
  quizItems: Item[];
  quizIdx: number;
  quizResults: boolean[];
  quizExercise: Exercise | null;
  quizPhase: "playing" | "awaiting" | "graded";
  quizSelected: string | null;
  quizTimer: number;
  onQuizAnswer: (optionId: string) => void;
  onAdvanceQuiz: () => void;
  onRetry: () => void;
  onPracticeAnyway: () => void;
  onReturnToToday: () => void;
}

function AcquisitionGateSection({
  gate,
  primaryGateNode,
  nodeInfo,
  listenRoute,
  quizItems,
  quizIdx,
  quizResults,
  quizExercise,
  quizPhase,
  quizSelected,
  quizTimer,
  onQuizAnswer,
  onAdvanceQuiz,
  onRetry,
  onPracticeAnyway,
  onReturnToToday,
}: AcquisitionGateSectionProps): ReactNode {
  const nodeTitle = nodeInfo?.title ?? primaryGateNode?.nodeId ?? "this skill";
  const threshold = primaryGateNode?.threshold ?? 20;
  const exposures = primaryGateNode?.exposures ?? 0;

  // Quiz: one question at a time with 8s timer
  if (gate === "quiz") {
    const isPlaying = quizPhase === "playing";
    const isAwaiting = quizPhase === "awaiting";
    const isGraded = quizPhase === "graded";
    const isLast = quizIdx + 1 >= quizItems.length;
    const correctSoFar = quizResults.filter(Boolean).length;

    return (
      <div className="bg-paperWarm border border-ink/10 border-l-4 border-l-success p-6 mb-4" role="region" aria-label="Bypass quiz">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-success">
            QUIZ — {nodeTitle}
          </p>
          <span className="font-mono text-[10px] text-inkFaded tabular-nums">
            {quizIdx + 1} / {quizItems.length}
            {isAwaiting && (
              <span className={`ml-3 ${quizTimer <= 3 ? "text-warning" : ""}`}>
                {quizTimer}s
              </span>
            )}
          </span>
        </div>

        {isPlaying && (
          <p className="font-serif italic text-sm text-inkSoft">Listening…</p>
        )}

        {(isAwaiting || isGraded) && quizExercise && (
          <>
            <p className="font-serif text-base text-ink mb-4">{quizExercise.prompt}</p>
            {quizExercise.exerciseType === "scale-degree-recognition" ? (
              <SolfegeButtons
                buttons={quizExercise.options.map((opt) => ({
                  syllable: opt.label,
                  selected: quizSelected === opt.id,
                  correct: isGraded && quizExercise.correctAnswerId === opt.id,
                  wrong: isGraded && quizSelected === opt.id && quizExercise.correctAnswerId !== opt.id,
                }))}
                disabled={isGraded}
                onSelect={(i) => {
                  const opt = quizExercise.options[i];
                  if (opt) onQuizAnswer(opt.id);
                }}
              />
            ) : (
              <MultipleChoiceOptions
                options={quizExercise.options}
                selectedId={quizSelected ?? undefined}
                correctId={quizExercise.correctAnswerId}
                graded={isGraded}
                disabled={!isAwaiting}
                onSelect={onQuizAnswer}
              />
            )}
          </>
        )}

        {isGraded && (
          <div className="mt-4 flex items-center justify-between">
            <p className="font-mono text-[10px] text-inkFaded">
              {correctSoFar + (quizResults[quizIdx - 1] !== undefined ? 0 : 0)} correct so far
            </p>
            {!isLast && (
              <button
                type="button"
                onClick={onAdvanceQuiz}
                className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
              >
                Next →
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Quiz pass
  if (gate === "quiz-pass") {
    const correctCount = quizResults.filter(Boolean).length;
    return (
      <div className="bg-paperWarm border border-ink/10 border-l-4 border-l-success p-6 mb-4" role="region" aria-label="Quiz passed">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-success mb-3">
          PASSED
        </p>
        <p className="font-serif text-xl font-medium text-ink mb-1">
          {correctCount} / {quizResults.length} correct
        </p>
        <p className="font-serif italic text-sm text-inkSoft mb-6">
          {nodeTitle} is unlocked. Your plan has been updated.
        </p>
        <button
          type="button"
          onClick={onReturnToToday}
          className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
        >
          ← Return to Today
        </button>
      </div>
    );
  }

  // Quiz fail
  if (gate === "quiz-fail") {
    const correctCount = quizResults.filter(Boolean).length;
    const remaining = threshold - exposures;
    return (
      <div className="bg-paperWarm border border-ink/10 border-l-4 border-l-warning p-6 mb-4" role="region" aria-label="Quiz result">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-warning mb-3">
          NOT QUITE
        </p>
        <p className="font-serif text-xl font-medium text-ink mb-1">
          {correctCount} / {quizResults.length} correct — need 8 to pass
        </p>
        <p className="font-serif italic text-sm text-inkSoft mb-4">
          {remaining > 0
            ? `~${remaining} more listens recommended before practicing.`
            : "A few more listens will reinforce it."}
        </p>

        <div className="flex gap-2 mb-5">
          {quizResults.map((correct, i) => (
            <span
              key={i}
              className={`w-5 h-5 flex items-center justify-center font-mono text-[9px] border ${
                correct ? "border-success text-success" : "border-warning text-warning"
              }`}
              aria-label={correct ? "correct" : "wrong"}
            >
              {correct ? "✓" : "✗"}
            </span>
          ))}
        </div>

        <div className="flex flex-col items-start gap-3">
          {listenRoute && (
            <a
              href={listenRoute}
              className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
            >
              Listen first →
            </a>
          )}
          <button
            type="button"
            onClick={onRetry}
            className="bg-transparent border border-ink/25 px-[18px] py-3 font-sans text-sm font-medium text-ink hover:bg-ink hover:text-paper transition-colors"
          >
            Try again →
          </button>
          <button
            type="button"
            onClick={onPracticeAnyway}
            className="font-mono text-[10px] text-inkFaded hover:text-ink transition-colors uppercase tracking-widest"
          >
            Practice anyway
          </button>
        </div>
      </div>
    );
  }

  return null;
}
